<template>
  <div class="settings-page">
    <h2>Menu</h2>
    <div class="subtitle">
      Customize the sidebar menu displayed with your own items.
    </div>
    <section>
      <!-- Global section: app_screen items (includes hidden ones for toggling) -->
      <BaseReorderableList
        :data="draggableGlobalData"
        class="menu-list global-section"
        tag="div"
        :item-key="(item: MenuItem) => getMenuItemKey(item)"
        root-class="menu-list"
        @change="handleGlobalReorder"
      >
        <template #item="{ item: item }">
          <MenuRow
            :item="item"
            :is-visible="isVisible(item)"
            @set-home="setHomeRoute"
            @unpin="unpinItem"
            @toggle="toggleAppScreen"
          />
        </template>
      </BaseReorderableList>

      <!-- Org sections: each section header is draggable to reorder orgs -->
      <draggable
        :list="draggableOrgSections"
        item-key="orgId"
        v-bind="orgDragOptions"
        handle=".org-drag-handle"
        @end="handleOrgSectionsReorder"
      >
        <template #item="{ element: section }">
          <div class="settings-org-section">
            <div class="org-section-header">
              <BaseButton kind="auxiliary" class="org-drag-handle handle">
                <template #icon><HandleIcon /></template>
              </BaseButton>
              <button
                class="collapse-btn"
                :aria-expanded="!isOrgCollapsed(section.orgId)"
                @click="toggleOrgCollapsed(section.orgId)"
              >
                <span class="org-name">{{ getOrgName(section.orgId) }}</span>
                <ChevronDownIcon
                  class="chevron"
                  :class="{
                    'chevron-collapsed': isOrgCollapsed(section.orgId),
                  }"
                />
              </button>
            </div>
            <!-- Settings list: app screens (Tasks, Pages, ...) interleaved
                 with pinned items, including hidden screens for toggling. -->
            <BaseReorderableList
              v-show="!isOrgCollapsed(section.orgId)"
              :data="getSectionData(section)"
              class="menu-list"
              tag="div"
              :item-key="(item: MenuItem) => getMenuItemKey(item)"
              root-class="menu-list"
              @change="(data) => handleOrgItemsReorder(section.orgId, data)"
            >
              <template #item="{ item: item }">
                <MenuRow
                  :item="item"
                  :is-visible="isVisible(item)"
                  @set-home="setHomeRoute"
                  @unpin="unpinItem"
                  @toggle="toggleAppScreen"
                />
              </template>
            </BaseReorderableList>
          </div>
        </template>
      </draggable>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { useRoute } from "vue-router";
import draggable from "vuedraggable/src/vuedraggable";

import type { MenuItem, OrgId } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseReorderableList from "@/components/base/BaseReorderableList.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import HandleIcon from "@/components/icons/HandleIcon.vue";
import { navigateToDefaultRoute } from "@/components/inject";
import * as inject from "@/components/inject";
import {
  getCanonicalMenuItemKey,
  getMenuItemKey,
  isGlobalMenuItem,
} from "@/components/menu/menuItems";
import { mergeReorderedMenuItemsForReplace } from "@/components/menu/menuReorder";
import {
  dedupeMenuItems,
  setOrgOrder,
} from "@/components/menu/useMenuSections";
import { useBrowserStore } from "@/composables/useBrowserStore";

import MenuRow from "./MenuRow.vue";

type OrgSectionData = {
  orgId: OrgId;
  mergedItems: MenuItem[];
  configItems: MenuItem[];
};

export default defineComponent({
  components: {
    BaseButton,
    BaseReorderableList,
    ChevronDownIcon,
    draggable,
    HandleIcon,
    MenuRow,
  },
  setup() {
    const route = useRoute();
    const menu = inject.menu();
    const controller = inject.controller();
    const profile = inject.profile();
    const isSaving = ref(false);

    const { syncState } = useBrowserStore(profile.value.user.accountId);
    // Collapsed org sections, keyed by org id (expanded by default).
    // Deliberately distinct from the sidebar's own collapsed state
    // (`menu:state`, see MenuPrimary.vue).
    const collapsedOrgs = syncState<Record<string, boolean>>(
      "settings/menu:collapsed",
      {}
    );

    const { orgSections } = menu;

    const personalOrgId = computed(
      () => profile.value.user.personal.organizationId
    );
    const visibleItemKeys = computed(
      () =>
        new Set(
          menu.visibleItems.value.map((item) =>
            getCanonicalMenuItemKey(item, personalOrgId.value)
          )
        )
    );

    const isVisible = (item: MenuItem): boolean =>
      visibleItemKeys.value.has(
        getCanonicalMenuItemKey(item, personalOrgId.value)
      );

    const isSettingsGlobalMenuItem = (item: MenuItem): boolean => {
      if (!isGlobalMenuItem(item)) return false;
      return true;
    };

    // Global section: app screens.
    // Hidden app screens stay here for toggling.
    const draggableGlobalData = ref<Array<{ items: MenuItem[] }>>([
      { items: [] },
    ]);

    watch(
      () =>
        dedupeMenuItems(menu.allItems.value, personalOrgId.value).filter(
          isSettingsGlobalMenuItem
        ),
      (items) => {
        draggableGlobalData.value[0].items = [...items];
      },
      { immediate: true }
    );

    // Org sections: each entry is draggable to reorder orgs
    const draggableOrgSections = ref<OrgSectionData[]>([]);

    watch(
      orgSections,
      (sections) => {
        draggableOrgSections.value = sections.map((s) => ({
          orgId: s.orgId,
          mergedItems: [...s.mergedItems],
          configItems: [...s.configItems],
        }));
      },
      { immediate: true }
    );

    // BaseReorderableList expects Array<{items}> — wrap section items on the
    // fly. Settings uses configItems so hidden app screens stay available for
    // re-enabling and retain their position.
    const getSectionData = (
      section: OrgSectionData
    ): Array<{ items: MenuItem[] }> => [{ items: section.configItems }];

    const getOrgName = (orgId: string): string =>
      profile.value.organizations.find((o) => o.id === orgId)?.name ??
      "Organization";

    const orgDragOptions = {
      animation: 200,
      forceFallback: true,
      fallbackClass: "drag-fallback",
      ghostClass: "drag-ghost",
      fallbackOnBody: true,
      fallbackTolerance: 3,
    };

    const save = async (items: MenuItem[]) => {
      isSaving.value = true;
      try {
        await controller.menuItems.replace(items);
        if (route.path === "/") {
          const navigateToDefault = navigateToDefaultRoute();
          navigateToDefault();
        }
      } catch (error) {
        window.console.error("Failed to save menu order:", error);
      } finally {
        isSaving.value = false;
      }
    };

    const handleGlobalReorder = async (data: Array<{ items: MenuItem[] }>) => {
      const visibleGlobal = (data[0]?.items ?? []).filter(isVisible);
      const previousVisibleGlobal = menu.visibleItems.value.filter(
        isSettingsGlobalMenuItem
      );
      await save(
        mergeReorderedMenuItemsForReplace({
          currentItems: menu.visibleItems.value,
          previousManagedItems: previousVisibleGlobal,
          reorderedManagedItems: visibleGlobal,
        })
      );
    };

    const handleOrgItemsReorder = async (
      orgId: OrgId,
      data: Array<{ items: MenuItem[] }>
    ) => {
      const reordered = data[0]?.items ?? [];
      const visibleMerged = reordered.filter(isVisible);
      const previousMerged =
        orgSections.value.find((s) => s.orgId === orgId)?.mergedItems ?? [];
      await save(
        mergeReorderedMenuItemsForReplace({
          currentItems: menu.visibleItems.value,
          previousManagedItems: previousMerged,
          reorderedManagedItems: visibleMerged,
        })
      );
    };

    const handleOrgSectionsReorder = () => {
      setOrgOrder(draggableOrgSections.value.map((s) => s.orgId));
    };

    const isOrgCollapsed = (orgId: string): boolean =>
      collapsedOrgs.value[orgId] ?? false;

    const toggleOrgCollapsed = (orgId: string) => {
      collapsedOrgs.value = {
        ...collapsedOrgs.value,
        [orgId]: !isOrgCollapsed(orgId),
      };
    };

    const toggleAppScreen = async (item: MenuItem) => {
      await menu.toggleAppScreenVisibility(item);
    };

    const setHomeRoute = async (item: MenuItem) => {
      await menu.setHomeItem(item);
    };

    const unpinItem = async (item: MenuItem) => {
      if (menu.isDatabase(item)) {
        await menu.unpinDatabase(item);
      } else if (menu.isAgent(item)) {
        await menu.unpinAgent(item);
      } else {
        await menu.unpinObject(item);
      }
    };

    return {
      draggableGlobalData,
      draggableOrgSections,
      getMenuItemKey,
      getOrgName,
      getSectionData,
      handleGlobalReorder,
      handleOrgItemsReorder,
      handleOrgSectionsReorder,
      isOrgCollapsed,
      isSaving,
      isVisible,
      menu,
      orgDragOptions,
      setHomeRoute,
      toggleAppScreen,
      toggleOrgCollapsed,
      unpinItem,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.global-section {
  padding-bottom: 6px;
  @include themed() {
    border-bottom: 1px solid t($grey-05);
  }
}

.settings-org-section {
  & + & {
    margin-top: 4px;
  }

  :deep(.reorder-item) {
    margin-inline-start: 28px;
  }
}

.org-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0 4px;

  .org-name {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-40);
    }
  }

  .handle {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  .collapse-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px;
    border: none;
    background: none;
    cursor: default;

    .chevron {
      flex-shrink: 0;
      width: 10px;
      height: 10px;
      @include transition(transform 150ms ease-out);

      :deep(.fill) {
        @include themed() {
          fill: t($grey-30);
        }
      }

      &.chevron-collapsed {
        transform: rotate(-90deg);
      }
    }
  }
}

.menu-list {
  display: flex;
  flex-direction: column;
}
</style>
